import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Settings as SettingsIcon, ClipboardList, LogOut, ExternalLink, CreditCard, Truck, LayoutList, Star, LayoutDashboard, Tag, Megaphone, Mail, Users } from "lucide-react";
import { adminMe } from "../lib/api";
import { useSite } from "../context/SiteContext";
import BrandLogo from "../components/BrandLogo";
import OverviewPanel from "../components/admin/OverviewPanel";
import ProductsPanel from "../components/admin/ProductsPanel";
import OrdersPanel from "../components/admin/OrdersPanel";
import StorePanel from "../components/admin/StorePanel";
import PaymentsPanel from "../components/admin/PaymentsPanel";
import ShippingPanel from "../components/admin/ShippingPanel";
import ContentPanel from "../components/admin/ContentPanel";
import ReviewsPanel from "../components/admin/ReviewsPanel";
import PromoPanel from "../components/admin/PromoPanel";
import MarketingPanel from "../components/admin/MarketingPanel";
import MessagesPanel from "../components/admin/MessagesPanel";
import CustomersPanel from "../components/admin/CustomersPanel";

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "orders", label: "Orders & Tracking", icon: ClipboardList },
  { id: "customers", label: "Customers", icon: Users },
  { id: "products", label: "Products", icon: Package },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "promos", label: "Promo Codes", icon: Tag },
  { id: "marketing", label: "Popup, Chat & Emails", icon: Megaphone },
  { id: "store", label: "Brand & Hero", icon: SettingsIcon },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "shipping", label: "Shipping", icon: Truck },
  { id: "content", label: "Content", icon: LayoutList },
  { id: "reviews", label: "Reviews", icon: Star },
];

const Admin = () => {
  const navigate = useNavigate();
  const { settings } = useSite();
  const [tab, setTab] = useState("overview");
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("mb_admin_token");
    if (!token) { navigate("/admin/login"); return; }
    adminMe().then((r) => { setEmail(r.email); setReady(true); }).catch(() => {
      localStorage.removeItem("mb_admin_token"); navigate("/admin/login");
    });
  }, [navigate]);

  const logout = () => { localStorage.removeItem("mb_admin_token"); navigate("/admin/login"); };

  if (!ready) return <div className="min-h-screen bg-navy flex items-center justify-center text-slate-400">Loading dashboard...</div>;

  return (
    <div className="min-h-screen bg-navy">
      <header className="bg-navy-800 border-b border-gold/15 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo settings={settings} size="h-9 w-9" testId="admin-logo" />
            <span className="font-display font-bold"><span className="liquid-gold-text">{settings?.brandName}</span> <span className="text-white">Admin</span></span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-gold transition-colors"><ExternalLink size={15} /> View Site</a>
            <span className="hidden md:inline text-xs text-slate-500">{email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm text-white border border-gold/30 hover:border-gold px-3 py-1.5 rounded-lg transition-colors"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-gold text-gold" : "border-transparent text-slate-400 hover:text-white"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "overview" && <OverviewPanel goTo={setTab} />}
        {tab === "products" && <ProductsPanel />}
        {tab === "orders" && <OrdersPanel />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "customers" && <CustomersPanel />}
        {tab === "promos" && <PromoPanel />}
        {tab === "marketing" && <MarketingPanel />}
        {tab === "store" && <StorePanel />}
        {tab === "payments" && <PaymentsPanel />}
        {tab === "shipping" && <ShippingPanel />}
        {tab === "content" && <ContentPanel />}
        {tab === "reviews" && <ReviewsPanel />}
      </div>
    </div>
  );
};

export default Admin;
